import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  Filter, 
  Download, 
  Eye, 
  Plus, 
  Trash2, 
  ChevronLeft, 
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Clock,
  Users,
  GraduationCap,
  Building,
  BookOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TIME_SLOTS = [
  '09:00-10:30',
  '10:30-12:00', 
  '12:00-13:30',
  '13:30-15:00',
  '15:00-16:30',
  '16:30-18:00'
];

const TimetableGenerator = () => {
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [timetableSlots, setTimetableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedBatch, setSelectedBatch] = useState(undefined);
  const [selectedTeacher, setSelectedTeacher] = useState(undefined);
  const [selectedClassroom, setSelectedClassroom] = useState(undefined);
  const [selectedStream, setSelectedStream] = useState(undefined);
  const [viewMode, setViewMode] = useState('batch'); // batch, teacher, classroom
  
  // Week navigation
  const [currentWeekStart, setCurrentWeekStart] = useState(getCurrentWeekStart());
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({
    batch_id: undefined,
    subject_id: undefined,
    teacher_id: undefined,
    classroom_id: undefined
  });

  useEffect(() => {
    Promise.all([
      fetchBatches(),
      fetchSubjects(), 
      fetchTeachers(),
      fetchClassrooms()
    ]).then(() => {
      fetchTimetableSlots();
    });
  }, [currentWeekStart, selectedBatch, selectedTeacher, selectedClassroom]);

  function getCurrentWeekStart() {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    return monday.toISOString().split('T')[0];
  }

  const fetchBatches = async () => {
    try {
      // Exclude archived batches from Timetable Generator
      const response = await axios.get(`${API}/batches`, {
        params: { include_archived: false }
      });
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API}/subjects`);
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API}/teachers`);
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const fetchClassrooms = async () => {
    try {
      const response = await axios.get(`${API}/classrooms`);
      setClassrooms(response.data);
    } catch (error) {
      console.error('Error fetching classrooms:', error);
    }
  };

  const fetchTimetableSlots = async () => {
    try {
      const params = new URLSearchParams({
        week_start_date: currentWeekStart
      });
      
      if (selectedBatch && selectedBatch !== 'all') params.append('batch_id', selectedBatch);
      if (selectedTeacher && selectedTeacher !== 'all') params.append('teacher_id', selectedTeacher);
      if (selectedClassroom && selectedClassroom !== 'all') params.append('classroom_id', selectedClassroom);
      
      const response = await axios.get(`${API}/timetable?${params}`);
      setTimetableSlots(response.data);
    } catch (error) {
      console.error('Error fetching timetable slots:', error);
      toast.error('Failed to load timetable');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotClick = (day, timeSlot) => {
    const existingSlot = getSlotData(day, timeSlot);
    
    if (existingSlot) {
      // Edit existing slot
      setFormData({
        batch_id: existingSlot.batch_id,
        subject_id: existingSlot.subject_id,
        teacher_id: existingSlot.teacher_id,
        classroom_id: existingSlot.classroom_id
      });
    } else {
      // Create new slot
      setFormData({
        batch_id: selectedBatch || undefined,
        subject_id: undefined,
        teacher_id: undefined,
        classroom_id: undefined
      });
    }
    
    setSelectedSlot({ day, timeSlot, existing: existingSlot });
    setIsModalOpen(true);
  };

  const handleSaveSlot = async (e) => {
    e.preventDefault();
    
    if (!selectedSlot) return;
    
    try {
      const slotData = {
        ...formData,
        day: selectedSlot.day,
        time_slot: selectedSlot.timeSlot,
        week_start_date: currentWeekStart
      };

      if (selectedSlot.existing) {
        // Delete existing slot first
        await axios.delete(`${API}/timetable/${selectedSlot.existing.id}`);
      }
      
      // Create new slot
      await axios.post(`${API}/timetable`, slotData);
      
      toast.success('Timetable slot saved successfully');
      setIsModalOpen(false);
      setSelectedSlot(null);
      setFormData({ batch_id: undefined, subject_id: undefined, teacher_id: undefined, classroom_id: undefined });
      fetchTimetableSlots();
      
    } catch (error) {
      console.error('Error saving slot:', error);
      if (error.response?.data?.detail) {
        toast.error(`Conflict: ${error.response.data.detail}`);
      } else {
        toast.error('Failed to save timetable slot');
      }
    }
  };

  const handleDeleteSlot = async () => {
    if (!selectedSlot?.existing) return;
    
    try {
      await axios.delete(`${API}/timetable/${selectedSlot.existing.id}`);
      toast.success('Timetable slot deleted successfully');
      setIsModalOpen(false);
      setSelectedSlot(null);
      fetchTimetableSlots();
    } catch (error) {
      console.error('Error deleting slot:', error);
      toast.error('Failed to delete timetable slot');
    }
  };

  const getSlotData = (day, timeSlot) => {
    return timetableSlots.find(slot => 
      slot.day === day && 
      slot.time_slot === timeSlot
    );
  };

  const getBatchName = (batchId) => {
    const batch = batches.find(b => b.id === batchId);
    return batch ? batch.name : 'Unknown Batch';
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'Unknown Subject';
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? teacher.name : 'Unknown Teacher';
  };

  const getClassroomName = (classroomId) => {
    const classroom = classrooms.find(c => c.id === classroomId);
    return classroom ? classroom.name : 'Unknown Classroom';
  };

  const navigateWeek = (direction) => {
    const currentDate = new Date(currentWeekStart);
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate.toISOString().split('T')[0]);
  };

  const getFilteredTeachers = () => {
    if (!formData.subject_id) return teachers;
    return teachers.filter(teacher => teacher.subjects.includes(formData.subject_id));
  };

  const getFilteredSubjects = () => {
    if (!selectedStream || selectedStream === 'all') return subjects;
    return subjects.filter(subject => subject.stream === selectedStream);
  };

  const getFilteredBatches = () => {
    // You can add stream-specific batch filtering here if needed
    return batches;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Timetable Generator</h1>
          <p className="text-gray-600">Create and manage weekly timetables</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline" 
            className="flex items-center"
            data-testid="export-timetable-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button 
            className="btn-primary flex items-center" 
            onClick={() => window.location.reload()}
            data-testid="refresh-timetable-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters & Controls */}
      <Card className="card-enhanced">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            {/* Stream Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Stream</label>
              <Select value={selectedStream} onValueChange={setSelectedStream}>
                <SelectTrigger data-testid="stream-filter-select">
                  <SelectValue placeholder="All Streams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Streams</SelectItem>
                  <SelectItem value="Interior Design">Interior Design</SelectItem>
                  <SelectItem value="Fashion Design">Fashion Design</SelectItem>
                  <SelectItem value="MBA Design Management">MBA Design Management</SelectItem>
                  <SelectItem value="Skill Enhancement">Skill Enhancement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* View Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">View Mode</label>
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger data-testid="view-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="batch">By Batch</SelectItem>
                  <SelectItem value="teacher">By Teacher</SelectItem>
                  <SelectItem value="classroom">By Classroom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Batch Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {viewMode === 'batch' ? 'Select Batch' : 'Filter by Batch'}
              </label>
              <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                <SelectTrigger data-testid="batch-filter-select">
                  <SelectValue placeholder="All Batches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Batches</SelectItem>
                  {batches.map(batch => (
                    <SelectItem key={batch.id} value={batch.id}>
                      {batch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Teacher Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {viewMode === 'teacher' ? 'Select Teacher' : 'Filter by Teacher'}
              </label>
              <Select value={selectedTeacher} onValueChange={setSelectedTeacher}>
                <SelectTrigger data-testid="teacher-filter-select">
                  <SelectValue placeholder="All Teachers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teachers</SelectItem>
                  {teachers.map(teacher => (
                    <SelectItem key={teacher.id} value={teacher.id}>
                      {teacher.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Classroom Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {viewMode === 'classroom' ? 'Select Classroom' : 'Filter by Classroom'}
              </label>
              <Select value={selectedClassroom} onValueChange={setSelectedClassroom}>
                <SelectTrigger data-testid="classroom-filter-select">
                  <SelectValue placeholder="All Classrooms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classrooms</SelectItem>
                  {classrooms.map(classroom => (
                    <SelectItem key={classroom.id} value={classroom.id}>
                      {classroom.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Week Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => navigateWeek(-1)}
              className="flex items-center"
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous Week
            </Button>
            
            <div className="text-center">
              <div className="font-semibold text-gray-900">
                Week of {new Date(currentWeekStart).toLocaleDateString()}
              </div>
              <div className="text-sm text-gray-500">
                {new Date(currentWeekStart).toLocaleDateString()} - {
                  new Date(new Date(currentWeekStart).getTime() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString()
                }
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={() => navigateWeek(1)}
              className="flex items-center"
              data-testid="next-week-btn"
            >
              Next Week
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Timetable Grid */}
      <Card className="card-enhanced overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Header Row */}
              <div className="grid grid-cols-7 bg-gray-50 border-b">
                <div className="p-4 font-medium text-gray-900 border-r">
                  <Clock className="w-4 h-4 inline mr-2" />
                  Time
                </div>
                {DAYS.map(day => (
                  <div key={day} className="p-4 font-medium text-gray-900 text-center border-r last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>

              {/* Time Slot Rows */}
              {TIME_SLOTS.map(timeSlot => (
                <div key={timeSlot} className="grid grid-cols-7 border-b last:border-b-0">
                  {/* Time Column */}
                  <div className="p-4 bg-gray-50 border-r font-medium text-sm text-gray-700 flex items-center">
                    {timeSlot}
                  </div>
                  
                  {/* Day Columns */}
                  {DAYS.map(day => {
                    const slotData = getSlotData(day, timeSlot);
                    return (
                      <div
                        key={`${day}-${timeSlot}`}
                        className={`p-2 border-r last:border-r-0 min-h-[80px] cursor-pointer transition-all duration-200 ${ 
                          slotData 
                            ? 'bg-blue-50 hover:bg-blue-100' 
                            : 'bg-white hover:bg-gray-50'
                        }`}
                        onClick={() => handleSlotClick(day, timeSlot)}
                        data-testid={`time-slot-${day}-${timeSlot}`}
                      >
                        {slotData && (
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-blue-800 flex items-center">
                              <Users className="w-3 h-3 mr-1" />
                              {getBatchName(slotData.batch_id)}
                            </div>
                            <div className="text-xs text-gray-600 flex items-center">
                              <BookOpen className="w-3 h-3 mr-1" />
                              {getSubjectName(slotData.subject_id)}
                            </div>
                            <div className="text-xs text-gray-600 flex items-center">
                              <GraduationCap className="w-3 h-3 mr-1" />
                              {getTeacherName(slotData.teacher_id)}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center">
                              <Building className="w-3 h-3 mr-1" />
                              {getClassroomName(slotData.classroom_id)}
                            </div>
                          </div>
                        )}
                        
                        {!slotData && (
                          <div className="flex items-center justify-center h-full text-gray-400">
                            <Plus className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Slot Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="slot-modal-title">
              {selectedSlot?.existing ? 'Edit Timetable Slot' : 'Add Timetable Slot'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveSlot} className="space-y-4">
            {selectedSlot && (
              <div className="bg-blue-50 p-3 rounded-lg mb-4">
                <div className="text-sm font-medium text-blue-800">
                  {selectedSlot.day} - {selectedSlot.timeSlot}
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Batch *
              </label>
              <Select value={formData.batch_id} onValueChange={(value) => setFormData({...formData, batch_id: value})}>
                <SelectTrigger data-testid="slot-batch-select">
                  <SelectValue placeholder="Select Batch" />
                </SelectTrigger>
                <SelectContent>
                  {batches.map(batch => (
                    <SelectItem key={batch.id} value={batch.id}>
                      {batch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <Select value={formData.subject_id} onValueChange={(value) => setFormData({...formData, subject_id: value})}>
                <SelectTrigger data-testid="slot-subject-select">
                  <SelectValue placeholder="Select Subject" />
                </SelectTrigger>
                <SelectContent>
                  {getFilteredSubjects().map(subject => (
                    <SelectItem key={subject.id} value={subject.id}>
                      {subject.name}
                      {subject.stream && ` (${subject.stream})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teacher *
              </label>
              <Select value={formData.teacher_id} onValueChange={(value) => setFormData({...formData, teacher_id: value})}>
                <SelectTrigger data-testid="slot-teacher-select">
                  <SelectValue placeholder="Select Teacher" />
                </SelectTrigger>
                <SelectContent>
                  {getFilteredTeachers().map(teacher => (
                    <SelectItem key={teacher.id} value={teacher.id}>
                      {teacher.name}
                      {formData.subject_id && !teacher.subjects.includes(formData.subject_id) && (
                        <span className="text-red-500 ml-2">(Not qualified)</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Classroom *
              </label>
              <Select value={formData.classroom_id} onValueChange={(value) => setFormData({...formData, classroom_id: value})}>
                <SelectTrigger data-testid="slot-classroom-select">
                  <SelectValue placeholder="Select Classroom" />
                </SelectTrigger>
                <SelectContent>
                  {classrooms.map(classroom => (
                    <SelectItem key={classroom.id} value={classroom.id}>
                      {classroom.name}
                      {classroom.location && ` (${classroom.location})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex space-x-3 pt-4">
              {selectedSlot?.existing && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDeleteSlot}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="delete-slot-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
              
              <div className="flex-1 flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1"
                  data-testid="cancel-slot-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 btn-primary"
                  data-testid="save-slot-btn"
                >
                  {selectedSlot?.existing ? 'Update' : 'Add'} Slot
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimetableGenerator;